import {
  Button,
  Loading,
  Title as RaTitle,
  useRedirect,
  useTranslate
} from 'react-admin'
import React, { ReactNode } from 'react'
import { isNil, map, split } from 'lodash/fp'
import { Buffer } from 'buffer'
import { SHA256, enc } from 'crypto-js'
import { addMinutes, format } from 'date-fns/fp'
import axios from 'axios'
import log from 'loglevel'
import {
  Grid,
  Typography,
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material'

import gmokeys from './gmo-keys.json'

log.setLevel(log.levels.DEBUG)

const env = process.env['REACT_APP_ENV']
const gmoBaseUrls = {
  prd: 'https://link.mul-pay.jp/v1/plus',
  stg: 'https://stg.link.mul-pay.jp/v1/plus',
  int: 'https://stg.link.mul-pay.jp/v1/plus',
  dev: 'https://stg.link.mul-pay.jp/v1/plus',
  exp: 'https://stg.link.mul-pay.jp/v1/plus'
}
const gmoBaseUrl = gmoBaseUrls[env as keyof typeof gmoBaseUrls]
const gmoRetUrls = !isNil(process.env['REACT_APP_APOLLO'])
  ? {
      prd: 'https://api-b2b-v2-v2.terashite.jp/partner-user-public/v1/gmopg',
      stg: 'https://api-b2b-v2-v2-stg.terashite.jp/partner-user-public/v1/gmopg',
      int: 'https://api-b2b-v2-v2-int.dev.terashite.jp/partner-user-public/v1/gmopg',
      dev: 'https://api-b2b-v2-v2.dev.terashite.jp/partner-user-public/v1/gmopg',
      exp: 'https://api-b2b-v2-v2-exp.dev.terashite.jp/partner-user-public/v1/gmopg'
    }
  : {
      prd: 'https://api-b2b-v2.terashite.jp/partner-user-public/v1/gmopg',
      stg: 'https://api-b2b-v2-stg.terashite.jp/partner-user-public/v1/gmopg',
      int: 'https://api-b2b-v2-int.dev.terashite.jp/partner-user-public/v1/gmopg',
      dev: 'https://api-b2b-v2.dev.terashite.jp/partner-user-public/v1/gmopg',
      exp: 'https://api-b2b-v2-exp.dev.terashite.jp/partner-user-public/v1/gmopg'
    }
const gmoRetUrl = gmoRetUrls[env as keyof typeof gmoRetUrls]
const gmoKey = gmokeys[env as keyof typeof gmokeys]
const gmoShopId = gmoKey.id
const gmoShopPassword = gmoKey.password

const getCardEditUrl = (
  id: string,
  configId: string,
  expireMinutes?: number
): string => {
  const now = new Date()
  const jsonParam = {
    geturlparam: {
      ShopID: gmoShopId,
      ShopPass: gmoShopPassword
    },
    configid: configId,
    member: {
      MemberID: id,
      Cardeditno: now.getTime(),
      SrcOrderID: null,
      RetUrl: `${gmoRetUrl}/${id}`,
      EditExpireDate: getEditExpireDate(now, expireMinutes),
      TdFlag: '3'
    },
    tds2: {
      Tds2InputShowFlag: 1,
      Tds2Type: '3'
    }
  }
  const base64Encode = Buffer.from(JSON.stringify(jsonParam)).toString('base64')
  const hash = SHA256(base64Encode + gmoShopPassword).toString(enc.Hex)
  const parameter = base64Encode + '.' + String(hash)
  const url = `${gmoBaseUrl}/${gmoShopId}/member/${parameter}`
  return url
}
const getEditExpireDate = (
  date: Date,
  expireMinutes?: number
): string | null => {
  if (isNil(expireMinutes)) {
    return null
  } else {
    return format('yyyyMMddHHmm')(addMinutes(expireMinutes)(date))
  }
}

export const PartnerRegisterCompleteForm: React.FC<{ id: string }> = ({
  id
}) => {
  const translate = useTranslate()
  return (
    <Card>
      <RaTitle title={translate('mine.registerCompleteFormTitle')} />
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: '1280px' }}>
            <Box
              sx={{
                backgroundColor: '#fff7e1',
                paddingTop: '2em',
                paddingBottom: '3em'
              }}>
              <Box sx={{ marginLeft: '0.5em', marginRight: '0.5em' }}>
                <Grid
                  container
                  justifyContent="center"
                  spacing={2}
                  sx={{
                    marginTop: '0.1em'
                  }}>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        marginTop: '0em'
                      }}>
                      {translate('mine.credit.finish')}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        marginTop: '0em'
                      }}>
                      {translate('mine.credit.next1')}
                      <a href={`/${id}`}>{translate('mine.credit.next2')}</a>
                      {translate('mine.credit.next3')}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
export const PartnerRegisterFailForm: React.FC<{ id: string }> = ({ id }) => {
  const [dialogOpen, setDialogOpen] = React.useState(true)
  const translate = useTranslate()
  return (
    <CardCreateOrEditDialog
      id={id}
      setDialogOpen={setDialogOpen}
      dialogOpen={dialogOpen}
      cardRegister>
      {map((item: string) => <Typography>{item}</Typography>)(
        split('\n')(translate('mine.credit.dialogs.registrationFail'))
      )}
    </CardCreateOrEditDialog>
  )
}
export const CardCreateOrEditDialog: React.FC<{
  children: ReactNode
  id: string
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  dialogOpen: boolean
  cardRegister?: boolean
  expireMinutes?: number
  setSentSMS?: React.Dispatch<React.SetStateAction<boolean>>
  sentSMS?: boolean
}> = ({
  children,
  id,
  setDialogOpen,
  dialogOpen,
  cardRegister,
  expireMinutes,
  setSentSMS,
  sentSMS
}) => {
  const translate = useTranslate()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [isWatingApiResult, setIsWatingApiResult] = React.useState(false)
  const redirect = useRedirect()
  // const { data, isLoading } = useGetOne('partners', {
  //   id
  // })
  // if (isLoading) {
  //   return <Loading />
  // }
  log.debug('[CardCreateOrEditDialog]', { cardRegister })
  const handleAccept = (): void => {
    setDialogOpen(false)
    if (!isNil(sentSMS) && sentSMS) {
      if (!isNil(setSentSMS)) setSentSMS(false)
      return
    }
    if (!isNil(cardRegister) && cardRegister) {
      redirect(getCardEditUrl(id, 'terashiteAreg'))
    } else {
      setIsWatingApiResult(true)
      const putData = {
        url: getCardEditUrl(id, 'terashiteAedit', expireMinutes)
      }
      axios({
        url: `${gmoRetUrl}/${id}`,
        method: 'PUT',
        data: putData
      })
        .then(() => {
          setIsWatingApiResult(false)
          setDialogOpen(true)
          if (!isNil(setSentSMS)) {
            setSentSMS(true)
          }
        })
        .catch(log.error)
    }
  }
  return isWatingApiResult ? (
    <Loading />
  ) : (
    <Dialog open={dialogOpen} fullScreen={fullScreen}>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>
          <Typography>{translate('mine.credit.dialogs.close')}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  )
}
